import {useErrorsStore, useMessagesStore, useNotificationsStore, useTooltipsStore} from "@/stores/ToastDataStore";
import {watch} from "vue";
import {POSITION, useToast} from "vue-toastification";
import ErrorsToast from "@/components/toasts/ErrorsToast.vue";
import NotificationsToast from "@/components/toasts/NotificationsToast.vue";
import type {ToastID} from "vue-toastification/src/types/index.ts";
import MessageToast from "@/components/toasts/MessageToast.vue";
import ClientCache from "@/utils/login/ClientCache.ts";
import TooltipsToast from "@/components/toasts/TooltipsToast.vue";

export const useToastManager = () => {
  const errorsStore = useErrorsStore();
  const notificationsStore = useNotificationsStore();
  const messagesStore = useMessagesStore();
  const tooltipsStore = useTooltipsStore();

  const NAMA_ERROR_TOAST = "nama-error-toast";
  const NAMA_MESSAGE_TOAST = "nama-message-toast";
  const NAMA_TOOLTIP_TOAST = "nama-tooltip-toast";
  const toast = useToast();

  watch(
    () => tooltipsStore.tooltips,
    (tooltips) => {
      let isToolTipPinned: boolean = ClientCache.isPinTooltip();
      if (tooltips.length > 0) {
        toast.dismiss(NAMA_TOOLTIP_TOAST);
        toast({
          component: TooltipsToast,
          props: {
            messages: tooltips
          }
        }, {
          position: ClientCache.getTooltipPosition() === "Bottom" ? POSITION.BOTTOM_CENTER : POSITION.TOP_CENTER,
          toastClassName: NAMA_TOOLTIP_TOAST,
          id: NAMA_TOOLTIP_TOAST,
          timeout: false,
          closeOnClick: false,
          draggable: false,
          icon: false,
        });
        if (isToolTipPinned)
          return;
        listenForClickOutside(NAMA_TOOLTIP_TOAST, NAMA_TOOLTIP_TOAST);
      }
    },
    {deep: true}
  );

  function showErrorsToast() {
    const errors = errorsStore.errors;
    if (errors.length < 1)
      return;
    toast.error({
      component: ErrorsToast,
      props: {
        errors: errors
      }
    }, {
      position: POSITION.TOP_CENTER,
      toastClassName: NAMA_ERROR_TOAST,
      id: NAMA_ERROR_TOAST,
      timeout: false,
      closeOnClick: false,
      draggable: false,
      icon: false,
    });
    listenForClickOutside(NAMA_ERROR_TOAST, NAMA_ERROR_TOAST);
  }

  function closeErrorToast() {
    toast.dismiss(NAMA_ERROR_TOAST);
  }

  function closeMessageToast() {
    toast.dismiss(NAMA_MESSAGE_TOAST);
  }

  function closeTooltipToast() {
    toast.dismiss(NAMA_TOOLTIP_TOAST);
  }

  watch(
    () => errorsStore.errors,
    () => showErrorsToast(),
    {deep: true}
  );


  function listenForClickOutside(toastId: ToastID, toastClassName: string) {
    const handler = (e: Event) => {
      const toastRoot = document.getElementsByClassName(toastClassName);
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-close-btn]"))
        toast.dismiss(toastId);
      if (toastRoot.length && toastRoot[0].contains(target))
        return;
      if (toastRoot.length)
        toast.dismiss(toastId);
      document.removeEventListener('click', handler, true);
    };
    document.addEventListener('click', handler, true);
  }

  watch(
    () => notificationsStore.notifications,
    (notifications) => {
      if (notifications.length > 0) {
        const toastId: ToastID = toast({
          component: NotificationsToast,
          props: {
            notifications: notifications
          }
        }, {
          position: POSITION.BOTTOM_RIGHT,
          toastClassName: "nama-notifications-toast",
          bodyClassName:"nama-notifications-body-toast",
          timeout: false,
          closeOnClick: false,
          draggable: false,
          icon: false,
        });
        listenForClickOutside(toastId, "nama-notifications-toast");
      }
    },
    {deep: true}
  );

  watch(
    () => messagesStore.messages,
    (messages) => {
      if (messages.length > 0) {
        toast.dismiss(NAMA_MESSAGE_TOAST);
        toast({
          component: MessageToast,
          props: {
            messages: messages
          }
        }, {
          position: POSITION.TOP_CENTER,
          toastClassName: NAMA_MESSAGE_TOAST,
          id: NAMA_MESSAGE_TOAST,
          timeout: false,
          closeOnClick: false,
          draggable: false,
          icon: false,
        });
        listenForClickOutside(NAMA_MESSAGE_TOAST, NAMA_MESSAGE_TOAST);
      }
    },
    {deep: true}
  );

  return {showErrorsToast, closeErrorToast, closeMessageToast, closeTooltipToast}
};
